import clsx from 'clsx';
import {
  Children,
  type ClipboardEventHandler,
  type FocusEvent,
  forwardRef,
  Fragment,
  isValidElement,
  type ReactNode,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { devLog } from '../../core/utils/logging.js';
import { useContainerFocus } from '../../forms/hooks/useContainerFocus.js';
import {
  ActionsMenu,
  ActionsMenuInternal,
  type ActionsMenuConfig,
  getActionsMenuSlotConfig,
} from './actions-menu/ActionsMenu.js';
import { Copy } from './actions-menu/Copy.js';
import { Download } from './actions-menu/Download.js';
import { CodeHighlight } from './CodeHighlight.js';
import * as styles from './CodeSnippet.sty.js';
import { CODESNIPPET_COMPONENT_NAME } from './constants.js';
import type { HighlightLanguage } from './HighlightExtended.js';

/**
 * Collects the snippet text out of `children`.
 * Only string and number children carry snippet text:
 *  slot elements and falsy conditional expressions are dropped.
 * Fragments are transparent wrappers, so their own children are collected in turn.
 * @param children - The children of the `CodeSnippet`.
 */
function extractSnippetText(children: ReactNode): string {
  return Children.toArray(children)
    .map((child) => {
      if (typeof child === 'string' || typeof child === 'number') {
        return `${child}`;
      }
      if (isValidElement<WithChildren>(child) && child.type === Fragment) {
        return extractSnippetText(child.props.children);
      }

      return '';
    })
    .join('');
}

/**
 * Props related to the height of the `CodeSnippet` component.
 * @public
 */
export type CodeSnippetHeightProps =
  | {
      /**
       * Height of the code container. If this is known in advance, then very long
       * code snippets will still be able to fit into the container while also
       * having scrolling enabled. Value is given in px.
       */
      maxHeight?: number;
      fullHeight?: false;
    }
  | {
      /**
       * Whether the code snippet should take up the available height of its parent container.
       * @defaultValue false
       */
      fullHeight: true;
      maxHeight?: never;
    };

/**
 * Props for the `CodeSnippet` component.
 * @public
 */
export type CodeSnippetProps = WithChildren &
  StylingProps &
  DataTestId &
  MaskingProps &
  AriaLabelingProps &
  BehaviorTrackingProps &
  CodeSnippetHeightProps & {
    /**
     * Code language to be used to run highlighting on the given code.
     */
    language?: HighlightLanguage;
    /**
     * Whether the copy button should be displayed.
     * @defaultValue true
     * @deprecated Will be removed. Use `CodeSnippet.ActionsMenu` with `CodeSnippet.Copy` instead.
     */
    allowCopy?: boolean;
    /**
     * Whether the copy button should be displayed.
     * @defaultValue true
     * @deprecated Will be removed. Use `CodeSnippet.ActionsMenu` with `CodeSnippet.Copy` instead.
     */
    showCopyAction?: boolean;
    /**
     * Whether line numbers should be shown.
     * @defaultValue true
     */
    showLineNumbers?: boolean;
    /**
     * Whether the content breaks into new lines automatically or not.
     * @defaultValue false
     */
    lineBreaks?: boolean;
    /**
     * Callback fired when copy button is clicked and the code snippet was copied.
     * @deprecated Will be removed. Use the `onCopy` prop of `CodeSnippet.Copy` instead.
     */
    onCopy?: () => void;
    /**
     * CodeSnippet layout size, 'default' for standard spacing and 'condensed' for reduced font size, padding and margins.
     * @defaultValue 'default'
     */
    size?: 'default' | 'condensed';
    /**
     * Called when the component receives focus.
     */
    onFocus?: (e: FocusEvent<HTMLDivElement>) => void;
    /**
     * Called when the component loses focus.
     */
    onBlur?: (e: FocusEvent<HTMLDivElement>) => void;
    /**
     * Handler that is called when the element's focus status changes.
     * @deprecated Use onFocus/onBlur instead.
     */
    onFocusChange?: (isFocused: boolean) => void;
  };

const CodeSnippetForwardRef = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  CodeSnippetProps
>((props, forwardedRef) => {
  const {
    children,
    language,
    // oxlint-disable-next-line typescript/no-deprecated
    allowCopy: allowCopyProp,
    // oxlint-disable-next-line typescript/no-deprecated
    showCopyAction: showCopyActionProp,
    showLineNumbers = true,
    lineBreaks = false,
    maxHeight,
    // oxlint-disable-next-line typescript/no-deprecated
    onCopy,
    size = 'default',
    fullHeight = false,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    style: consumerStyle,
    className: consumerClassName,
    onFocus,
    onBlur,
    ...remainingProps
  } = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const ariaLabelingProps = useAriaLabelingProps(restProps);
  const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

  const slotConfig = getActionsMenuSlotConfig(children);

  // DEPRECATION: [APPDEV-16570] BREAKING-CHANGE: `showCopyAction`, `allowCopy` and the
  // top-level `onCopy` go away together with this fallback, leaving
  // `CodeSnippet.ActionsMenu` as the only way to opt into the Copy action.
  const showCopyAction = showCopyActionProp ?? allowCopyProp ?? true;
  if (slotConfig !== undefined && onCopy !== undefined) {
    devLog(
      'CodeSnippet: the `onCopy` prop is ignored when `CodeSnippet.ActionsMenu` is used. Pass `onCopy` to `CodeSnippet.Copy` instead.',
    );
  }
  const deprecatedCopyConfig: ActionsMenuConfig = showCopyAction
    ? { copyConfig: { onCopy } }
    : {};
  const actionsConfig = slotConfig ?? deprecatedCopyConfig;

  const { containerClassName, ...containerFocusProps } =
    useContainerFocus<HTMLDivElement>({
      onFocus,
      onBlur,
    });

  const snippetContent = useMemo(
    () => extractSnippetText(children),
    [children],
  );

  const copySelection: ClipboardEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      const selection = document.getSelection();
      const clipboardData = event.nativeEvent.clipboardData;
      if (selection === null) {
        return;
      }
      if (clipboardData === null) {
        devLog(
          'CodeSnippet: clipboard access was blocked (restricted iframe or strict CSP). Nothing was copied.',
        );
        return;
      }
      // Firefox tends to add a new line separator per div (with user-select: none; css style rule)
      // in the selection if the selection spans multiple divs (one div per line).
      // See https://bugzilla.mozilla.org/show_bug.cgi?id=1273836
      clipboardData.setData('text/plain', selection.toString());
      event.preventDefault();
    },
    [],
  );

  const codeHighlightRef = useRef<HTMLPreElement | null>(null);

  return (
    <div
      ref={forwardedRef}
      onCopy={copySelection}
      className={clsx(consumerClassName, containerClassName, styles.wrapper, {
        [styles.wrapperFullHeight]: fullHeight,
        [styles.wrapperGutter({ size })]: showLineNumbers,
      })}
      style={consumerStyle}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {...containerFocusProps}
      {...ariaLabelingProps}
      {...behavioralTrackingProps}
      {...forwardProps}
      data-dt-component={CODESNIPPET_COMPONENT_NAME}
    >
      <CodeHighlight
        ref={codeHighlightRef}
        language={language}
        code={snippetContent}
        showLineNumbers={showLineNumbers}
        lineBreaks={lineBreaks}
        maxHeight={maxHeight}
        size={size}
      />
      <ActionsMenuInternal
        config={actionsConfig}
        size={size}
        snippetContent={snippetContent}
        language={language}
      />
    </div>
  );
});

(
  CodeSnippetForwardRef as typeof CodeSnippetForwardRef & {
    displayName: string;
  }
).displayName = CODESNIPPET_COMPONENT_NAME;

/**
 * Use the `CodeSnippet` to display a code block in a read-only context.
 * By default, the code is formatted but not highlighted.
 * @public
 */
export const CodeSnippet = Object.assign(CodeSnippetForwardRef, {
  ActionsMenu,
  Copy,
  Download,
});
